import React from "react";
import { useTranslate } from "@refinedev/core";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import type { EventType, UnifiedHistoryFilters } from "../../../types/events";

interface HistoryFiltersProps {
  filters: UnifiedHistoryFilters;
  onChange: (filters: UnifiedHistoryFilters) => void;
}

const EVENT_TYPE_OPTIONS: { value: EventType; labelKey: string; fallback: string }[] = [
  { value: "alert", labelKey: "history.type.alert", fallback: "Alert" },
  { value: "journey", labelKey: "history.type.journey", fallback: "Journey" },
  { value: "forbidden_zone", labelKey: "history.type.forbiddenZone", fallback: "Forbidden Zone" },
];

export const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  filters,
  onChange,
}) => {
  const translate = useTranslate();

  const handleSearchChange = (value: string) => {
    onChange({ ...filters, search: value || undefined });
  };

  const handleEventTypeChange = (value: EventType | null) => {
    onChange({ ...filters, eventType: value || undefined });
  };

  const handleDateFromChange = (value: Dayjs | null) => {
    onChange({
      ...filters,
      dateFrom: value ? value.startOf("day").toISOString() : undefined,
    });
  };

  const handleDateToChange = (value: Dayjs | null) => {
    onChange({
      ...filters,
      dateTo: value ? value.endOf("day").toISOString() : undefined,
    });
  };

  const handleHideActiveChange = (checked: boolean) => {
    onChange({ ...filters, hideActive: checked });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          label={translate("history.filters.search", "Search")}
          placeholder={translate("history.filters.searchPlaceholder", "Search by user name...")}
          value={filters.search || ""}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
        />

        <Autocomplete
          options={EVENT_TYPE_OPTIONS}
          value={EVENT_TYPE_OPTIONS.find((o) => o.value === filters.eventType) || null}
          onChange={(_, option) => handleEventTypeChange(option?.value || null)}
          getOptionLabel={(option) => translate(option.labelKey, option.fallback)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate("history.filters.eventType", "Event Type")}
              placeholder={translate("history.filters.eventTypePlaceholder", "All types")}
              size="small"
            />
          )}
          sx={{ minWidth: 180 }}
        />

        <DatePicker
          label={translate("history.filters.dateFrom", "From")}
          value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
          onChange={handleDateFromChange}
          slotProps={{ textField: { size: "small" } }}
        />

        <DatePicker
          label={translate("history.filters.dateTo", "To")}
          value={filters.dateTo ? dayjs(filters.dateTo) : null}
          onChange={handleDateToChange}
          slotProps={{ textField: { size: "small" } }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={filters.hideActive !== false}
              onChange={(_, checked) => handleHideActiveChange(checked)}
              size="small"
            />
          }
          label={translate("history.filters.hideActive", "Hide active events")}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            onChange({ hideActive: true })
          }
        >
          {translate("history.filters.reset", "Reset")}
        </Button>
      </Box>
    </LocalizationProvider>
  );
};
